import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import {
  UtensilsCrossed, Plus, Loader2, Pencil, Trash2, Sparkles, X, Save, ArrowLeft, ShieldCheck, Search,
} from 'lucide-react';
import EditableCategoryChip from '../components/EditableCategoryChip';
import { toast } from 'sonner';
import { CategorySelect, VariationsEditor, ImageUploader } from '../components/MenuFormControls';
import { DietaryTagPicker, DietaryTagIcons } from '../components/DietaryTags';
import { formatCurrency } from '../lib/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const blankItem = () => ({
  group: '',
  category: 'Mains',
  name: '',
  description: '',
  photo_url: '',
  price: 0,
  is_special: false,
  sort_order: 0,
  variations: [],
  dietary_tags: [],
});

const AdminClientMenuPage = () => {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [items, setItems] = useState([]);
  const [categoryOrders, setCategoryOrders] = useState({}); // { name: sort_order }
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | item
  const [draft, setDraft] = useState(blankItem());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/clients/${clientId}/menu/items`);
      setClient(res.data.client);
      setItems(res.data.items || []);
      const orderMap = {};
      const ords = res.data.category_orders || {};
      Object.keys(ords).forEach((name) => {
        orderMap[name] = Number(ords[name]) || 0;
      });
      setCategoryOrders(orderMap);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load menu');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setDraft(blankItem()); setEditing('new'); };
  const openEdit = (item) => {
    setDraft({
      category: item.category || 'Menu',
      name: item.name || '',
      description: item.description || '',
      photo_url: item.photo_url || '',
      price: item.price || 0,
      is_special: !!item.is_special,
      sort_order: item.sort_order || 0,
      variations: Array.isArray(item.variations) ? item.variations : [],
      dietary_tags: Array.isArray(item.dietary_tags) ? item.dietary_tags : [],
    });
    setEditing(item);
  };
  const closeEdit = () => { setEditing(null); setDraft(blankItem()); };

  const save = async () => {
    if (!draft.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editing === 'new') {
        await axios.post(`${API}/admin/clients/${clientId}/menu/items`, draft);
        toast.success('Item added');
      } else {
        await axios.put(`${API}/admin/clients/${clientId}/menu/items/${editing.id}`, draft);
        toast.success('Item updated');
      }
      await load();
      closeEdit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/clients/${clientId}/menu/items/${item.id}`);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      toast.success('Deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  // Category bulk operations — these update the on-disk `category` field
  // across multiple items, so we reload from server after each to keep the
  // chip counts and grouping accurate.
  const renameCategory = async (oldName, newName) => {
    try {
      const res = await axios.put(`${API}/admin/clients/${clientId}/menu/categories`, {
        old: oldName, new: newName,
      });
      toast.success(`Renamed "${oldName}" → "${res.data.new}" (${res.data.updated} item${res.data.updated === 1 ? '' : 's'})`);
      // If the user was filtering by the old category, keep them on the renamed one.
      setActiveCategory((cur) => (cur === oldName ? res.data.new : cur));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rename failed');
      throw e; // signal the chip to stay in edit mode
    }
  };
  const deleteCategory = async (name) => {
    try {
      const res = await axios.delete(`${API}/admin/clients/${clientId}/menu/categories`, {
        data: { category: name },
      });
      toast.success(`Deleted "${name}" + ${res.data.deleted} item${res.data.deleted === 1 ? '' : 's'}`);
      // If they were filtered by the deleted category, drop back to All.
      setActiveCategory((cur) => (cur === name ? 'All' : cur));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
      throw e;
    }
  };

  // Persist a sub-category's display order (white-glove). `label` reflects
  // the RENAMED value when rename+reorder are saved in the same edit action.
  const setCategoryOrder = async (nextOrder, label) => {
    try {
      await axios.put(`${API}/admin/clients/${clientId}/menu/categories/order`, {
        category: label,
        sort_order: Number(nextOrder) || 0,
      });
      setCategoryOrders((prev) => ({ ...prev, [label]: Number(nextOrder) || 0 }));
      toast.success(`"${label}" order set to ${Number(nextOrder) || 0}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Order save failed');
      throw e;
    }
  };

  const existingGroups = [...new Set(items.map((i) => i.group).filter(Boolean))];
  const existingCategories = [...new Set(items.map((i) => i.category).filter(Boolean))].sort();

  // ─── Search + category filter + group-by-category ──────────────────
  // A real client menu can run 80–200 items. Without these three knobs the
  // flat grid becomes scroll-hell and an admin can't tell what's been
  // captured per section.
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  // Guard: the Menu feature can be toggled OFF for a client from
  // /admin/clients. When it's off we still allow super-admin editing (so
  // menus can be pre-loaded before the feature goes live), but we surface
  // a banner explaining that guest-facing menu pages stay hidden until
  // the toggle is flipped on.
  const featureMenusOff = client?.features?.menus === false;

  // Counts per category for the chip badges (built from the full unfiltered list).
  const categoryCounts = useMemo(() => {
    const counts = { All: items.length };
    for (const it of items) {
      const c = it.category || 'Uncategorized';
      counts[c] = (counts[c] || 0) + 1;
    }
    return counts;
  }, [items]);

  // Sort chips + item groups by admin-set sort_order ASC, then alphabetically.
  const categoryChips = useMemo(() => {
    const cats = Array.from(new Set(items.map((i) => i.category || 'Uncategorized')));
    cats.sort((a, b) => {
      const ao = categoryOrders[a] || 0;
      const bo = categoryOrders[b] || 0;
      if (ao !== bo) return ao - bo;
      return a.localeCompare(b, undefined, { sensitivity: 'base' });
    });
    return ['All', ...cats];
  }, [items, categoryOrders]);

  // Apply search + category filter, then group what remains by category.
  const groupedItems = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matches = items.filter((it) => {
      if (activeCategory !== 'All' && (it.category || 'Uncategorized') !== activeCategory) return false;
      if (!needle) return true;
      return (
        (it.name || '').toLowerCase().includes(needle)
        || (it.description || '').toLowerCase().includes(needle)
        || (it.category || '').toLowerCase().includes(needle)
      );
    });
    const groups = new Map();
    for (const it of matches) {
      const c = it.category || 'Uncategorized';
      if (!groups.has(c)) groups.set(c, []);
      groups.get(c).push(it);
    }
    // Sort categories by admin-set sort_order ASC, then alphabetically for
    // predictable scanning.
    return Array.from(groups.entries()).sort(([a], [b]) => {
      const ao = categoryOrders[a] || 0;
      const bo = categoryOrders[b] || 0;
      if (ao !== bo) return ao - bo;
      return a.localeCompare(b);
    });
  }, [items, query, activeCategory, categoryOrders]);
  const matchedCount = groupedItems.reduce((sum, [, list]) => sum + list.length, 0);

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8 pb-32 max-w-5xl mx-auto" data-testid="admin-client-menu-page">
        {/* Breadcrumb / header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/admin/clients" className="text-gray-500 hover:text-white transition-colors" data-testid="admin-menu-back">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <UtensilsCrossed className="w-6 h-6 text-[#00FFFF] flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-[10px] text-gray-500 uppercase tracking-[0.18em] font-bold flex items-center gap-1.5">
                <ShieldCheck className="w-3 h-3 text-[#FF007F]" />
                White-glove · Admin
              </p>
              <h1 className="text-2xl lg:text-3xl font-extrabold text-white tracking-tight truncate font-['Outfit']">
                {client ? `${client.name}'s Menu` : 'Menu'}
              </h1>
            </div>
          </div>
          <Button
            onClick={openNew}
            className="bg-[#00FFFF] hover:bg-[#00d4d4] text-black rounded-full px-5 text-xs font-bold tracking-wider"
            data-testid="admin-menu-add-item"
          >
            <Plus className="w-4 h-4 mr-1.5" /> ADD ITEM
          </Button>
        </div>

        {featureMenusOff && (
          <div className="mb-4 p-3 rounded-2xl bg-[#FFB800]/10 border border-[#FFB800]/30 text-[#FFB800] text-xs flex items-center gap-2" data-testid="admin-menu-feature-off-notice">
            <Sparkles className="w-4 h-4" />
            Menu feature is currently <span className="font-bold">OFF</span> for this client. Items you add here are saved but won&apos;t show on the guest menu until you flip Menu ON in <Link to="/admin/clients" className="underline">/admin/clients</Link>.
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#00FFFF] animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 bg-white/[0.03] border border-white/10 rounded-3xl">
            <UtensilsCrossed className="w-12 h-12 text-gray-700 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">No items yet. Click <span className="text-[#00FFFF] font-semibold">ADD ITEM</span> to start.</p>
          </div>
        ) : (
          <>
            {/* Search + category filter toolbar — sticky so it stays in view
                as the admin scrolls down a long menu. */}
            <div className="sticky top-0 z-10 bg-[#000000]/95 backdrop-blur-md -mx-4 lg:-mx-8 px-4 lg:px-8 py-3 mb-4 border-b border-white/8">
              <div className="flex items-center gap-2 mb-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search items, descriptions or categories…"
                    className="pl-9 pr-9 h-10 bg-white/[0.04] border-white/10 text-white rounded-xl text-sm placeholder:text-gray-600"
                    data-testid="admin-menu-search"
                  />
                  {query && (
                    <button
                      onClick={() => setQuery('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-md text-gray-500 hover:text-white hover:bg-white/5"
                      data-testid="admin-menu-search-clear"
                      aria-label="Clear search"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <span className="text-[10px] uppercase tracking-[0.18em] text-gray-500 font-bold font-mono whitespace-nowrap" data-testid="admin-menu-count">
                  {matchedCount === items.length
                    ? `${items.length} items`
                    : `${matchedCount} / ${items.length}`}
                </span>
              </div>

              {/* Category chips with live counts + inline rename/delete/order */}
              <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-thin items-center" data-testid="admin-menu-category-chips">
                {categoryChips.map((cat) => (
                  <EditableCategoryChip
                    key={cat}
                    label={cat}
                    count={categoryCounts[cat] || 0}
                    active={cat === activeCategory}
                    isAllChip={cat === 'All'}
                    accentColor="#00FFFF"
                    sortOrder={categoryOrders[cat] || 0}
                    onSelect={() => setActiveCategory(cat)}
                    onRename={(next) => renameCategory(cat, next)}
                    onDelete={() => deleteCategory(cat)}
                    onSortOrderChange={(nextOrder, label) => setCategoryOrder(nextOrder, label || cat)}
                  />
                ))}
              </div>
            </div>

            {matchedCount === 0 ? (
              <div className="text-center py-16 bg-white/[0.03] border border-white/10 rounded-3xl">
                <Search className="w-10 h-10 text-gray-700 mx-auto mb-2" />
                <p className="text-gray-400 text-sm">No items match <span className="text-white">&ldquo;{query}&rdquo;</span></p>
                <button
                  onClick={() => { setQuery(''); setActiveCategory('All'); }}
                  className="mt-3 text-[#00FFFF] text-xs font-bold hover:underline"
                  data-testid="admin-menu-clear-filters"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {groupedItems.map(([category, list]) => (
                  <section key={category} data-testid={`admin-menu-section-${category}`}>
                    <div className="flex items-baseline justify-between mb-2 px-1">
                      <h2 className="text-[11px] uppercase tracking-[0.3em] text-[#00FFFF] font-bold font-['JetBrains_Mono']">
                        {category}
                      </h2>
                      <span className="text-[10px] text-gray-600 font-mono">{list.length} item{list.length === 1 ? '' : 's'}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {list.map((item) => (
                        <div
                          key={item.id}
                          className="flex gap-3 p-3 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-white/20 transition-colors"
                          data-testid={`admin-menu-item-${item.id}`}
                        >
                          {item.photo_url ? (
                            <img src={item.photo_url} alt={item.name} className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-16 h-16 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0">
                              <UtensilsCrossed className="w-6 h-6 text-gray-600" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <h3 className="text-white font-semibold text-sm truncate font-['Outfit']">{item.name}</h3>
                                  {item.is_special && <Sparkles className="w-3 h-3 text-[#FF007F] flex-shrink-0" />}
                                  <DietaryTagIcons value={item.dietary_tags} size={11} className="flex-shrink-0" />
                                </div>
                                {item.description && <p className="text-gray-400 text-xs truncate mt-0.5">{item.description}</p>}
                              </div>
                              {(() => {
                                const hasVars = Array.isArray(item.variations) && item.variations.length > 0;
                                if (hasVars) {
                                  const prices = item.variations.map((v) => Number(v.price) || 0).filter((p) => p > 0);
                                  const min = prices.length ? Math.min(...prices) : 0;
                                  const max = prices.length ? Math.max(...prices) : 0;
                                  return (
                                    <p className="text-[#00FFFF] font-bold text-xs whitespace-nowrap font-mono" title={`${item.variations.length} variants`}>
                                      {min === max ? formatCurrency(min) : `${formatCurrency(min)}–${formatCurrency(max)}`}
                                    </p>
                                  );
                                }
                                return item.price > 0
                                  ? <p className="text-[#00FFFF] font-bold text-sm whitespace-nowrap font-mono">{formatCurrency(item.price)}</p>
                                  : null;
                              })()}
                            </div>
                            <div className="flex gap-1.5 mt-2">
                              <Button onClick={() => openEdit(item)} variant="ghost" className="h-7 px-2.5 text-[11px] rounded-full text-gray-300 hover:text-white hover:bg-white/5" data-testid={`admin-menu-edit-${item.id}`}>
                                <Pencil className="w-3 h-3 mr-1" /> Edit
                              </Button>
                              <Button onClick={() => remove(item)} variant="ghost" className="h-7 px-2.5 text-[11px] rounded-full text-[#FF3B3B] hover:bg-[#FF3B3B]/10" data-testid={`admin-menu-delete-${item.id}`}>
                                <Trash2 className="w-3 h-3 mr-1" /> Delete
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit/Create modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeEdit}>
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-[#0a0a0a] border border-white/10 rounded-3xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
            data-testid="admin-menu-edit-modal"
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-extrabold text-white font-['Outfit']">
                {editing === 'new' ? 'Add Menu Item' : 'Edit Menu Item'}
              </h2>
              <button onClick={closeEdit} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-400 mb-1 block font-semibold tracking-wider uppercase">Menu Group</label>
                <CategorySelect
                  value={draft.group || ''}
                  onChange={(v) => setDraft({ ...draft, group: v })}
                  options={existingGroups}
                  testIdPrefix="admin-menu-edit-group"
                />
                <p className="text-[10px] text-gray-600 mt-1">Top-level bucket — e.g. Food, Bar, Desserts. Type to create a new one.</p>
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block font-semibold tracking-wider uppercase">Category</label>
                <CategorySelect
                  value={draft.category}
                  onChange={(v) => setDraft({ ...draft, category: v })}
                  options={existingCategories}
                  testIdPrefix="admin-menu-edit"
                />
                <p className="text-[10px] text-gray-600 mt-1">Section within the group — e.g. Beers, Cocktails, Mains.</p>
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block font-semibold tracking-wider uppercase">Name *</label>
                <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Truffle Burger" className="bg-black/40 border-white/10 text-white h-10 rounded-xl" data-testid="admin-menu-edit-name" />
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block font-semibold tracking-wider uppercase">Description</label>
                <textarea
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  rows={3}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm placeholder:text-gray-600 resize-none focus:outline-none focus:border-[#00FFFF]/40"
                  placeholder="Optional"
                  data-testid="admin-menu-edit-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-400 mb-1 block font-semibold tracking-wider uppercase">
                    Base Price (₹)
                    {draft.variations?.length > 0 && (
                      <span className="ml-1 text-[10px] text-gray-600 normal-case">— hidden when variations exist</span>
                    )}
                  </label>
                  <Input type="number" min="0" step="0.01" value={draft.price} onChange={(e) => setDraft({ ...draft, price: parseFloat(e.target.value) || 0 })} className="bg-black/40 border-white/10 text-white h-10 rounded-xl font-mono" data-testid="admin-menu-edit-price" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block font-semibold tracking-wider uppercase">Sort</label>
                  <Input type="number" value={draft.sort_order} onChange={(e) => setDraft({ ...draft, sort_order: parseInt(e.target.value, 10) || 0 })} className="bg-black/40 border-white/10 text-white h-10 rounded-xl font-mono" data-testid="admin-menu-edit-sort" />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block font-semibold tracking-wider uppercase">Photo</label>
                <ImageUploader value={draft.photo_url} onChange={(url) => setDraft({ ...draft, photo_url: url })} testIdPrefix="admin-menu-edit" />
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block font-semibold tracking-wider uppercase">Variations</label>
                <VariationsEditor value={draft.variations} onChange={(v) => setDraft({ ...draft, variations: v })} testIdPrefix="admin-menu-edit" />

                <label className="text-xs uppercase text-gray-400 tracking-widest pt-2 block">Dietary / Tags</label>
                <p className="text-[10px] text-gray-500 mb-1.5 leading-snug">
                  Sets the dish&apos;s <em>base</em> identity (e.g. a green salad is <strong>Veg</strong>). Each variation above can override this — e.g. adding a &ldquo;Chicken&rdquo; variation and tagging it <strong>Non-veg</strong>.
                </p>
                <DietaryTagPicker
                  value={draft.dietary_tags || []}
                  onChange={(tags) => setDraft({ ...draft, dietary_tags: tags })}
                  testIdPrefix="admin-menu-edit"
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-xl bg-black/30 border border-white/5">
                <div>
                  <p className="text-white text-sm font-semibold flex items-center gap-2">
                    Special <Sparkles className="w-3 h-3 text-[#FF007F]" />
                  </p>
                  <p className="text-gray-500 text-[11px]">Pinned to a &quot;Specials&quot; tab on the guest menu.</p>
                </div>
                <Switch
                  checked={draft.is_special}
                  onCheckedChange={(v) => setDraft({ ...draft, is_special: v })}
                  className="data-[state=checked]:bg-[#FF007F]"
                  data-testid="admin-menu-edit-special"
                />
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <Button onClick={closeEdit} variant="ghost" className="flex-1 border border-white/10 rounded-full text-xs font-semibold tracking-wider text-gray-300">CANCEL</Button>
              <Button onClick={save} disabled={saving} className="flex-1 bg-[#00FFFF] hover:bg-[#00d4d4] text-black rounded-full text-xs font-bold tracking-wider" data-testid="admin-menu-edit-save">
                {saving ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                SAVE
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminClientMenuPage;
